/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Heading, Text, VFlow } from 'bold-ui'
import { isProcedimentoSisab } from 'components/form/field/select/ProcedimentoSelectField/ProcedimentoSelectFieldItem'
import { LoadingIndicator } from 'components/loading'
import { useHistoricoProcedimentoDwQuery } from 'graphql/hooks.generated'
import { FatoProcedAtend } from 'graphql/types.generated'
import { isEmpty, partition } from 'lodash'
import {
  HistoricoDwPanelProps,
  historicoSubsectionStyles,
} from 'view/atendimentos/detail/historico/model/model-historico'
import {
  convertFatoMedicoesToHistoricoMedicaoModel,
  hasMedicoes,
  mountInfosAdministrativas,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'

import HistoricoSection from '../../../../HistoricoSection'
import HistoricoDetailTable from '../../../../table/HistoricoDetailTable'
import HistoricoGapPanel from '../../HistoricoGapPanel'
import { HistoricoIvcfTableBox } from '../../HistoricoIvcfTableBox'
import HistoricoMedicoesTableBox from '../../HistoricoMedicoesTableBox'
import HistoricoNoDetailAvailable from '../../HistoricoNoDetailAvailable'

export default function HistoricoProcedimentoDw({
  uuidRegistro,
  cpfOuCnsCidadao,
  idadeOrigemAtendimento,
}: Readonly<HistoricoDwPanelProps>) {
  const {
    data: { historicoProcedimentoDw: detalheProcedimentoDw },
    loading,
  } = useHistoricoProcedimentoDwQuery({
    variables: {
      input: {
        uuidRegistro,
        cpfOuCnsCidadao,
        isOrigemPec: false,
      },
    },
  })

  if (loading) return <LoadingIndicator />

  if (!detalheProcedimentoDw) return <HistoricoNoDetailAvailable />

  const [procedimentosSisab, procedimentosSigtap] = partition(detalheProcedimentoDw.procedimentos, (procedimento) =>
    isProcedimentoSisab(procedimento.codigoProcedimento)
  )

  const medicoes = convertFatoMedicoesToHistoricoMedicaoModel(detalheProcedimentoDw as FatoProcedAtend)

  const hasAnyMedicao = hasMedicoes(medicoes)
  const hasIvcf = !isEmpty(detalheProcedimentoDw.ivcf)

  return (
    <HistoricoGapPanel
      idadeOrigemAtendimento={idadeOrigemAtendimento}
      informacoesAdministrativas={mountInfosAdministrativas(detalheProcedimentoDw)}
    >
      <VFlow>
        <HistoricoSection section='S'>
          <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
        </HistoricoSection>
        <HistoricoSection section='O'>
          <VFlow>
            {!hasAnyMedicao && !hasIvcf && <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>}
            {hasAnyMedicao && <HistoricoMedicoesTableBox sectionStyle='O' medicoes={medicoes} />}
            {hasIvcf && (
              <HistoricoIvcfTableBox
                ivcf={detalheProcedimentoDw.ivcf}
                riscoVulnerabilidadeText={detalheProcedimentoDw.ivcf.dimensaoGrauVulnerabilidadeIvcf.descricao}
              />
            )}
          </VFlow>
        </HistoricoSection>
        <HistoricoSection section='P'>
          {!isEmpty(procedimentosSisab) ? (
            <VFlow vSpacing={0.5}>
              <Heading level={5} style={historicoSubsectionStyles.plano}>
                Intervenções e/ou procedimentos clínicos
              </Heading>
              <HistoricoDetailTable hasLines>
                <VFlow>
                  {procedimentosSisab.map((procedimento) => (
                    <Text>
                      {procedimento.descricaoProcedimento} - {procedimento.codigoProcedimento}
                    </Text>
                  ))}
                </VFlow>
              </HistoricoDetailTable>
            </VFlow>
          ) : (
            <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
          )}
        </HistoricoSection>
        <HistoricoSection section='D'>
          {!isEmpty(procedimentosSigtap) ? (
            <VFlow vSpacing={0.5}>
              <Heading level={5}>Procedimentos Administrativos (SIGTAP)</Heading>
              <HistoricoDetailTable hasLines>
                <VFlow>
                  {procedimentosSigtap.map((procedimento) => (
                    <Text key={procedimento.id}>
                      {procedimento.descricaoProcedimento} - {procedimento.codigoProcedimento}
                    </Text>
                  ))}
                </VFlow>
              </HistoricoDetailTable>
            </VFlow>
          ) : (
            <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
          )}
        </HistoricoSection>
      </VFlow>
    </HistoricoGapPanel>
  )
}
